import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button as ConfirmButton,
  DialogContentText
} from '@p81-common/ui';

import {
  PageSubTitle,
  Button,
  SelectDropDown
} from 'components/common/Elements/Elements';
import Checkbox from 'components/common/Elements/Checkbox/Checkbox';
import InputArea from 'components/common//forms/InputArea/InputArea';
import { getRequest, postRequest } from 'utils/requests';
import {
  validationNoSpecialCharacters,
  validationFeatureFlagName,
  hasFeatureFlagNameContent
} from 'utils/validation';
import { showNotif } from 'utils/notification';
import { useRegion } from 'contexts/RegionContext';
import { useAuthState } from 'contexts/auth-context';
import { useOwnerOptions } from 'hooks/useOwnerOptions';
import { featureFlagsStatuses } from '../../helpers/configurates';
import RegionStatusRow from './RegionStatusRow';
import usePerRegionStyles from './perRegion.styles';
import classes from './ModalFeature.module.scss';

const ModalFeature = ({
  data,
  setIsOpenAdditional,
  requestForFilteredData
}) => {
  const { availableRegions, region } = useRegion();
  const allRegionCodes = availableRegions.map(r => r.code);
  const { ownerOptions } = useOwnerOptions();
  const { auth } = useAuthState();
  const pr = usePerRegionStyles();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ownerName, setOwnerName] = useState(data ? data.ownerName || '' : '');
  // Create-only: which regions the flag is created in.
  const [selectedRegions, setSelectedRegions] = useState(
    data ? [] : allRegionCodes
  );
  // Edit-only: per-region status. `fetchedStates` is what the API returned;
  // `drafts` holds the user's per-row status/removal changes on top of it.
  const [fetchedStates, setFetchedStates] = useState(null);
  // True when per-region statuses couldn't be loaded in full (older API or a
  // failed fetch): rows we don't have a real status for must stay read-only, or
  // a GA region could be downgraded without the confirm dialog.
  const [statesUnavailable, setStatesUnavailable] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [phase, setPhase] = useState('editing'); // editing | applying | results
  const [results, setResults] = useState(null); // { [region]: { ok, failureKind } }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { t } = useTranslation(['featureFlags']);

  const ownerEmail = ownerOptions.find(
    option => option.value === ownerName
  )?.email;
  const ownerChanged = ownerName !== (data?.ownerName || '');

  // Edit: derive the five region rows from what the API returned plus the
  // user's drafts. Kept out of the fetch effect so region metadata (a fixed
  // set) never has to be an effect dependency.
  const rows = useMemo(() => {
    const byRegion = {};
    (fetchedStates || []).forEach(s => {
      byRegion[s.region] = s;
    });

    return availableRegions.map(r => {
      const state = byRegion[r.code];
      const exists = Boolean(state);
      const originalStatus = exists ? state.status : null;
      const draft = drafts[r.code] || {};
      // When the fetch was incomplete, a region we have no state for has an
      // unknown status (not "not set") and must stay read-only.
      const statusUnknown = statesUnavailable && !state;

      return {
        region: r.code,
        exists,
        originalStatus,
        statusUnknown,
        customerIdsCount: exists ? state.customerIdsCount || 0 : 0,
        draftStatus:
          draft.draftStatus !== undefined ? draft.draftStatus : originalStatus
      };
    });
  }, [availableRegions, fetchedStates, drafts, statesUnavailable]);

  const statusChangedRows = rows.filter(
    r => r.draftStatus && r.draftStatus !== r.originalStatus
  );
  const destructiveRows = rows.filter(
    r =>
      r.originalStatus === featureFlagsStatuses.GlobalAssigned &&
      r.draftStatus !== featureFlagsStatuses.GlobalAssigned
  );
  const editHasChanges =
    statusChangedRows.length > 0 ||
    data?.name !== name ||
    data?.description !== description ||
    ownerChanged;

  const applyDisabled = data
    ? phase !== 'editing' ||
      !hasFeatureFlagNameContent(name) ||
      !description ||
      !editHasChanges
    : !hasFeatureFlagNameContent(name) ||
      !description ||
      selectedRegions.length === 0 ||
      !ownerName;

  useEffect(() => {
    if (!data) {
      return;
    }
    setName(data.name);
    setDescription(data.description);
    const actingOnly = [
      {
        region: region.code,
        status: data.status,
        customerIdsCount: data.customerIdsCount || 0
      }
    ];
    getRequest(`/product/fflag/${data._id}/regions`)
      .then(res => {
        const states = res?.body?.regionStates;
        if (states?.length) {
          setFetchedStates(states);
          setStatesUnavailable(false);
        } else {
          // Older API without per-region status: we only know the acting region.
          // Mark the rest unavailable so they can't be edited blindly.
          setFetchedStates(actingOnly);
          setStatesUnavailable(true);
        }
      })
      .catch(() => {
        setFetchedStates(actingOnly);
        setStatesUnavailable(true);
        showNotif({
          message: t('featureFlags:REGIONS_FETCH_ERROR'),
          kind: 'error',
          dismissAfter: 5000,
          type: 'alert'
        });
      });
  }, [data, region.code, t]);

  // On create, default the owner to the logged-in user once the options land.
  useEffect(() => {
    if (data || ownerName || !ownerOptions.length) {
      return;
    }
    const currentUser = ownerOptions.find(
      option => option.email === auth?.user?.email
    );

    if (currentUser) {
      setOwnerName(currentUser.value);
    }
  }, [data, ownerName, ownerOptions, auth]);

  // While the confirm dialog is open, keep Escape from bubbling to the
  // AdditionalModal wrapper's document listener (which would close the whole
  // feature modal). Capture phase runs before that bubble-phase listener.
  useEffect(() => {
    if (!confirmOpen) {
      return undefined;
    }
    const stopEscFromClosingParent = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setConfirmOpen(false);
      }
    };
    document.addEventListener('keydown', stopEscFromClosingParent, true);
    return () =>
      document.removeEventListener('keydown', stopEscFromClosingParent, true);
  }, [confirmOpen]);

  const toggleRegion = code => {
    setSelectedRegions(prev =>
      prev.includes(code) ? prev.filter(r => r !== code) : [...prev, code]
    );
  };

  const handleStatusChange = (code, status) => {
    setDrafts(prev => ({
      ...prev,
      [code]: { ...prev[code], draftStatus: status }
    }));
  };

  const ownerPayload =
    ownerName && ownerEmail && (!data || ownerChanged)
      ? { ownerName, ownerEmail }
      : {};

  // Create: unchanged legacy behavior.
  const submitCreate = () => {
    postRequest('/product/fflag/save', {
      name,
      description,
      regions: selectedRegions,
      ...ownerPayload
    })
      .then(() => {
        requestForFilteredData();
        setIsOpenAdditional(false);
      })
      .catch(err => {
        setIsOpenAdditional(false);
        showNotif({
          message: err.response.data.message,
          kind: 'error',
          dismissAfter: 5000,
          type: 'alert'
        });
      });
  };

  // Edit: per-region status. `onlyRegion` builds a single-row retry payload.
  const submitEdit = onlyRegion => {
    const scope = onlyRegion ? rows.filter(r => r.region === onlyRegion) : rows;
    const regionsPayload = scope
      .filter(r => r.draftStatus && r.draftStatus !== r.originalStatus)
      .map(r => ({ region: r.region, status: r.draftStatus }));

    setConfirmOpen(false);
    setPhase('applying');

    postRequest('/product/fflag/save', {
      id: data._id,
      name,
      description,
      regions: regionsPayload,
      ...ownerPayload
    })
      .then(() => {
        requestForFilteredData();
        if (onlyRegion) {
          setResults(prev => {
            const next = { ...(prev || {}), [onlyRegion]: { ok: true } };
            return next;
          });
          setPhase('results');
        } else {
          const changed = regionsPayload.map(r => r.region).join(', ');
          showNotif({
            message: changed
              ? t('featureFlags:UPDATE_SUCCESS_TOAST', { regions: changed })
              : t('featureFlags:UPDATE_SUCCESS'),
            kind: 'success',
            dismissAfter: 5000,
            type: 'alert'
          });
          setIsOpenAdditional(false);
        }
      })
      .catch(err => {
        const resultsArr = err?.response?.data?.body?.results;
        if (err?.response?.status === 424 && Array.isArray(resultsArr)) {
          requestForFilteredData();
          // Only surface regions the user actually changed. The API also
          // returns the untouched regions (name/description propagation), but
          // showing "Updated" on a row whose status didn't change is misleading.
          const changedRegions = new Set(regionsPayload.map(r => r.region));
          setResults(prev => {
            const next = { ...(prev || {}) };
            resultsArr
              .filter(r => changedRegions.has(r.region))
              .forEach(r => {
                next[r.region] = r;
              });
            return next;
          });
          setPhase('results');
        } else {
          showNotif({
            message: err?.response?.data?.message || 'Save failed',
            kind: 'error',
            dismissAfter: 5000,
            type: 'alert'
          });
          // Drop stale per-region results so old captions don't linger when we
          // return to editing.
          setResults(null);
          setPhase('editing');
        }
      });
  };

  const onSubmit = e => {
    e.preventDefault();
    if (!data) {
      submitCreate();
    } else if (destructiveRows.length > 0) {
      setConfirmOpen(true);
    } else {
      submitEdit();
    }
  };

  const handleOwner = e => setOwnerName(e);

  const handleName = e => {
    if (validationFeatureFlagName(e.target.value)) {
      setName(e.target.value);
    }
  };

  const handleDescription = e => {
    if (validationNoSpecialCharacters(e.target.value)) {
      setDescription(e.target.value);
    }
  };

  // Cancel discards unsaved edits back to the original values; the X and
  // clicking outside close the panel. On create there is nothing to revert to.
  const handleCancel = () => {
    if (!data) {
      setIsOpenAdditional(false);
      return;
    }
    setName(data.name);
    setDescription(data.description);
    setOwnerName(data.ownerName || '');
    setDrafts({});
  };

  const succeeded = results
    ? Object.values(results).filter(r => r.ok).length
    : 0;
  const attention = results
    ? Object.values(results).filter(r => !r.ok).length
    : 0;

  return (
    <>
      <form onSubmit={onSubmit}>
        <PageSubTitle
          title={
            data
              ? `${t('featureFlags:EDIT_FEATURE')}:`
              : `${t('featureFlags:ADD_FEATURE')}:`
          }
          className={classes.title}
          classNameHeading={classes.heading}
          size={2}
        />
        <div className={classes.wrap}>
          <InputArea
            percentMode={false}
            iconSearch={false}
            title={`${t('featureFlags:FEATURE_NAME')}:`}
            value={name}
            onChange={handleName}
            clearButton={false}
            placeholder={`${t('featureFlags:ENTER_FEATURE_NAME')}:`}
            classNameScheme={{
              wrap: classes.wrapInput,
              input: classes.input,
              title: classes.titleInput
            }}
          />
          <InputArea
            percentMode={false}
            iconSearch={false}
            modeTextArea={true}
            title={`${t('featureFlags:DESCRIPTION')}:`}
            value={description}
            onChange={handleDescription}
            maxLength={200}
            clearButton={false}
            placeholder={t('featureFlags:ENTER_DESCRIPTION')}
            classNameScheme={{
              input: classes.input,
              title: classes.titleInput
            }}
            visibleCounter={false}
          />
          <SelectDropDown
            searchable
            clearable={false}
            className={classes.owner}
            options={ownerOptions}
            searchKeys={['value', 'email']}
            renderKey={'label'}
            reload
            input={{
              value: ownerName,
              onChange: handleOwner
            }}
            label={t('featureFlags:OWNER')}
            classTitle={classes.titleInput}
          />
          {data ? (
            <div className={classes.regions}>
              <p className={pr.subtitle}>
                {t('featureFlags:STATUS_PER_REGION_SUBTITLE')}
              </p>
              {phase === 'results' && (
                <div className={pr.banner}>
                  {t('featureFlags:RESULTS_BANNER', {
                    count: succeeded + attention,
                    succeeded,
                    attention
                  })}
                </div>
              )}
              <div className={pr.rows}>
                {rows.map(row => (
                  <RegionStatusRow
                    key={row.region}
                    row={row}
                    t={t}
                    disabled={phase !== 'editing' || row.statusUnknown}
                    result={results?.[row.region]}
                    onStatusChange={handleStatusChange}
                    onRetry={phase === 'results' ? submitEdit : undefined}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className={classes.regions}>
              <p className={classes.regionsLabel}>{`${t(
                'featureFlags:REGIONS'
              )}:`}</p>
              <div className={classes.regionsList}>
                {availableRegions.map(r => (
                  <Checkbox
                    key={r.code}
                    value={r.code}
                    checked={selectedRegions.includes(r.code)}
                    onChange={() => toggleRegion(r.code)}
                    disabledMode={false}
                  />
                ))}
              </div>
            </div>
          )}
          {!(data && phase === 'results') && (
            <div className={classes.buttons}>
              <Button
                onClick={handleCancel}
                type="button"
                className={classes.buttons_cancel}
              >
                {t('featureFlags:CANCEL')}
              </Button>
              <Button
                className={classes.buttons_add}
                disabled={applyDisabled}
                type="submit"
              >
                {data
                  ? t('featureFlags:APPLY_FEATURE')
                  : t('featureFlags:ADD_NEW_FEATURE')}
              </Button>
            </div>
          )}
        </div>
      </form>
      <Modal
        id="applyChangesConfirmModal"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        dialogTitleProps={{
          children: t('featureFlags:APPLY_CHANGES_TITLE', {
            count: statusChangedRows.length
          })
        }}
        dialogContentProps={{
          children: (
            <DialogContentText>
              {t('featureFlags:APPLY_CHANGES_BODY', {
                count: destructiveRows.length
              })}
              {destructiveRows.map(r => (
                <span key={r.region} className={pr.confirmItem}>
                  <strong>{r.region}:</strong>{' '}
                  {t('featureFlags:CONFIRM_DOWNGRADE', {
                    from: t(`featureFlags:${r.originalStatus}`),
                    to: t(`featureFlags:${r.draftStatus}`)
                  })}
                </span>
              ))}
            </DialogContentText>
          )
        }}
        dialogActionsProps={{
          children: (
            <>
              <ConfirmButton
                variant="text"
                color="primary"
                onClick={() => setConfirmOpen(false)}
              >
                {t('featureFlags:CANCEL')}
              </ConfirmButton>
              <ConfirmButton color="error" onClick={() => submitEdit()}>
                {t('featureFlags:APPLY_CHANGES_CTA')}
              </ConfirmButton>
            </>
          )
        }}
      />
    </>
  );
};

ModalFeature.propTypes = {
  data: PropTypes.object,
  setIsOpenAdditional: PropTypes.func.isRequired,
  requestForFilteredData: PropTypes.func.isRequired
};

export default ModalFeature;
